import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import VideoWithSpeed from '../../components/VideoWithSpeed';
import { publicApi, DenemeBookDetail } from '../../services/api';
import { groupDenemeVideos, flattenDenemeGroups, DenemeGroupVideo } from '../../utils/denemeVideoGrouping';

const F = { fontFamily: 'Inter' } as const;

// Public solution-video viewer for a single "Deneme Sınavı" book. The API ships
// exam books in two shapes (see utils/denemeVideoGrouping) — one section per
// subject, or one section per question — so the payload is normalised into
// subject groups first and the page only ever renders "ders başlığı + Soru N"
// tiles. Videos only — no PDF, no login, no progress tracking.
export default function DenemeSinaviBook() {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [book, setBook] = useState<DenemeBookDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeGroup, setActiveGroup] = useState<string | null>(null); // null = "Tümü"
  const [playingId, setPlayingId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    publicApi.denemeBookContent(bookId!)
      .then((r) => { if (alive) setBook(r.data); })
      .catch((e) => { if (alive) { if (e?.response?.status === 404) setNotFound(true); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [bookId]);

  const groups = useMemo(() => groupDenemeVideos(book?.sections ?? []), [book]);
  const visibleGroups = useMemo(
    () => (activeGroup ? groups.filter((g) => g.key === activeGroup) : groups),
    [groups, activeGroup],
  );
  // The player walks every video on the page, so "Sonraki" carries on from
  // Türkçe 40 into Sosyal Bilimler 1 instead of dead-ending at a subject edge.
  const playlist = useMemo(() => flattenDenemeGroups(visibleGroups), [visibleGroups]);
  const playIdx = useMemo(
    () => (playingId ? playlist.findIndex((v) => v.id === playingId) : -1),
    [playlist, playingId],
  );
  const active: DenemeGroupVideo | null = playIdx >= 0 ? playlist[playIdx] : null;
  const totalVideos = useMemo(() => groups.reduce((n, g) => n + g.videos.length, 0), [groups]);

  const closePlayer = useCallback(() => setPlayingId(null), []);
  const go = useCallback((delta: number) => {
    setPlayingId((cur) => {
      if (cur == null) return cur;
      const i = playlist.findIndex((v) => v.id === cur);
      const next = i + delta;
      return next >= 0 && next < playlist.length ? playlist[next].id : cur;
    });
  }, [playlist]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePlayer();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, go, closePlayer]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f7fb] flex items-center justify-center" style={F}>
        <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !book) {
    return (
      <div className="min-h-screen bg-[#f6f7fb] flex flex-col items-center justify-center gap-4 px-6 text-center" style={F}>
        <p className="text-[18px] font-semibold text-[#2b2b2b]">Deneme sınavı bulunamadı.</p>
        <button onClick={() => navigate('/deneme-sinavi-videolari')} className="text-[#3973bf] font-semibold hover:underline">
          ← Tüm deneme sınavları
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f7fb]" style={F}>
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => navigate('/deneme-sinavi-videolari')}
              className="text-[14px] font-semibold text-[#3973bf] hover:underline whitespace-nowrap"
            >
              ← Geri
            </button>
            <div className="min-w-0">
              <h1 className="text-[15px] lg:text-[18px] font-semibold text-[#2b2b2b] truncate">{book.display_name || book.title}</h1>
              {totalVideos > 0 && (
                <p className="text-[12px] text-[#888] truncate">{totalVideos} soru çözüm videosu</p>
              )}
            </div>
          </div>
          <BrandLogo variant="mark" className="h-[30px] lg:h-[34px] object-contain shrink-0" />
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 lg:px-8 py-7">
        {groups.length === 0 ? (
          <p className="text-center text-gray-400 py-20">Bu deneme için henüz çözüm videosu eklenmemiş.</p>
        ) : (
          <>
            {/* Subject filter — "Tümü" keeps every ders stacked under each other. */}
            {groups.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-3 mb-5 -mx-1 px-1">
                <FilterChip active={activeGroup === null} onClick={() => setActiveGroup(null)} label="Tümü" count={totalVideos} />
                {groups.map((g) => (
                  <FilterChip
                    key={g.key}
                    active={activeGroup === g.key}
                    onClick={() => setActiveGroup(g.key)}
                    label={g.title}
                    count={g.videos.length}
                  />
                ))}
              </div>
            )}

            <div className="flex flex-col gap-8">
              {visibleGroups.map((g) => (
                <section key={g.key}>
                  <div className="flex items-baseline gap-2.5 mb-3">
                    <h2 className="text-[16px] lg:text-[19px] font-semibold text-[#2b2b2b]">{g.title}</h2>
                    <span className="text-[12px] lg:text-[13px] text-[#888]">{g.videos.length} video</span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 xl:grid-cols-10 gap-2.5">
                    {g.videos.map((v) => (
                      <button
                        key={v.id}
                        onClick={() => setPlayingId(v.id)}
                        className="group bg-white rounded-[12px] border border-gray-100 px-2 py-3 flex flex-col items-center gap-1.5 hover:border-[#e5599b] hover:shadow-md transition-all"
                        title={`${g.title} · ${v.label}`}
                      >
                        <span className="w-8 h-8 rounded-full bg-[#fdecf4] text-[#e5599b] flex items-center justify-center text-[12px] group-hover:bg-[#e5599b] group-hover:text-white transition-colors">▶</span>
                        <span className="text-[12px] font-medium text-[#2b2b2b] text-center leading-tight truncate w-full">{v.label}</span>
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Full-screen player */}
      {active && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col" style={F}>
          <div className="flex items-center justify-between px-5 lg:px-8 h-[60px] border-b border-gray-100 shrink-0">
            <div className="min-w-0">
              <p className="text-[12px] text-[#888] truncate">{active.groupTitle}</p>
              <p className="text-[15px] font-semibold text-[#2b2b2b] truncate">{active.label}</p>
            </div>
            <button onClick={closePlayer} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-gray-100 shrink-0" aria-label="Kapat">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
                <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>
          </div>

          <div className="flex-1 min-h-0 bg-black flex items-center justify-center">
            <VideoWithSpeed src={active.url} autoPlay wrapperClassName="w-full h-full flex flex-col items-center justify-center" className="max-h-[80vh] max-w-full" />
          </div>

          {/* Nav — the strip lists the current ders only, so a 125-soru deneme
              doesn't render 125 jump buttons under the player. */}
          <div className="flex items-center justify-between px-5 lg:px-8 h-[64px] border-t border-gray-100 shrink-0 gap-4">
            <button
              onClick={() => go(-1)}
              disabled={playIdx <= 0}
              className="rounded-[12px] px-5 py-2.5 text-[14px] font-semibold text-[#2b2b2b] bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              ← Önceki
            </button>
            <PlayerStrip playlist={playlist} activeId={active.id} onPick={setPlayingId} />
            <button
              onClick={() => go(1)}
              disabled={playIdx === playlist.length - 1}
              className="rounded-[12px] px-5 py-2.5 text-[14px] font-semibold text-white bg-[#3973bf] hover:bg-blue-700 disabled:opacity-40"
            >
              Sonraki →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-4 py-2 text-[13px] lg:text-[14px] font-medium border transition-colors ${
        active ? 'bg-[#3973bf] text-white border-[#3973bf]' : 'bg-white text-[#2b2b2b] border-gray-200 hover:bg-gray-50'
      }`}
    >
      {label}
      <span className={`ml-2 text-[11px] ${active ? 'text-white/80' : 'text-[#999]'}`}>{count}</span>
    </button>
  );
}

/** Horizontal jump strip limited to the playing video's own ders, auto-scrolled
 *  so the active soru stays visible as you page through with ← / →. */
function PlayerStrip({ playlist, activeId, onPick }: { playlist: DenemeGroupVideo[]; activeId: string; onPick: (id: string) => void }) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const group = playlist.find((v) => v.id === activeId)?.groupKey ?? '';
  const siblings = useMemo(() => playlist.filter((v) => v.groupKey === group), [playlist, group]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [activeId]);

  return (
    // `w-max mx-auto` on the inner row rather than justify-center on the
    // scroller: a centred flex container clips its own overflow start, which
    // put Soru 1–7 permanently out of reach on a 40-soru ders.
    <div className="flex-1 min-w-0 overflow-x-auto">
      <div className="flex items-center gap-1.5 px-1 w-max mx-auto">
        {siblings.map((v) => (
          <button
            key={v.id}
            ref={v.id === activeId ? activeRef : undefined}
            onClick={() => onPick(v.id)}
            title={v.label}
            className={`w-8 h-8 rounded-lg text-[12px] font-semibold shrink-0 ${
              v.id === activeId ? 'bg-[#e5599b] text-white' : 'bg-gray-100 text-[#2b2b2b] hover:bg-gray-200'
            }`}
          >
            {v.number ?? '▶'}
          </button>
        ))}
      </div>
    </div>
  );
}
